import { useEffect, useMemo, useState } from 'react';
import { ProSidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import 'react-pro-sidebar/dist/css/styles.css';
import { tokens } from '../../theme';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import PublicIcon from '@mui/icons-material/Public';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FlightIcon from '@mui/icons-material/Flight';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FeedbackIcon from '@mui/icons-material/Feedback';
import MessageIcon from '@mui/icons-material/Message';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AssessmentIcon from '@mui/icons-material/Assessment';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import { can, getAdminUser, isCustomRole } from '../../utils/permissions';

const Item = ({ title, to, icon, selected, setSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      onClick={() => setSelected(title)}
      {...(icon ? { icon } : {})}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

/** Whether the current path belongs under the Reports submenu. */
function isReportsSubmenuPath(pathname) {
  if (pathname.startsWith('/reports/my-reports')) return false;
  if (pathname === '/reports/survey-results' || pathname === '/reports/survey-responses') return false;
  return (
    pathname === '/reports' ||
    pathname.startsWith('/reports/') ||
    pathname === '/trip-reports' ||
    pathname.startsWith('/trip-reports/') ||
    pathname === '/reports-by-user' ||
    pathname.startsWith('/reports-by-user/')
  );
}

/** Whether the current path belongs under the Feedback Reports submenu. */
function isFeedbackReportsSubmenuPath(pathname) {
  return pathname === '/reports/survey-results' || pathname === '/reports/survey-responses';
}

const Sidebar = ({ mobileOpen = false, setMobileOpen = () => {} }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState('Dashboard');
  const [adminUser, setAdminUser] = useState(() => ({ username: '', role: '', ...getAdminUser() }));
  // Custom roles only see the menus they have permission for; other roles keep their fixed menus.
  const customRole = isCustomRole(adminUser);
  const show = (permissions) => can(permissions, adminUser);
  const reportItems = [
    { title: '- EXCURSION', to: '/reports/excursion', perm: 'reports.excursion.view' },
    { title: '- EXCURSION ATTENDANCE', to: '/reports/excursion-attendance', perm: 'reports.excursion_attendance.view' },
    { title: '- EXCURSION SUMMARY', to: '/reports/excursion-summary', perm: 'reports.excursion_summary.view' },
    { title: '- DESTINATION', to: '/reports/destinations', perm: 'reports.destination.view' },
    { title: '- TRIP', to: '/trip-reports', perm: 'reports.trip.view' },
    { title: '- GUIDE PERFORMANCE', to: '/reports/guide-performance', perm: 'reports.guide_performance.view' },
    { title: '- REPORT BY USER', to: '/reports-by-user', perm: 'reports.report_by_user.view' },
  ].filter((r) => show(r.perm));
  const feedbackReportItems = [
    { title: '- SURVEY RESULTS', to: '/reports/survey-results', perm: 'reports.survey_results.view' },
    { title: '- SURVEY RESPONSES', to: '/reports/survey-responses', perm: 'reports.survey_responses.view' },
  ].filter((r) => show(r.perm));
  const navigate = useNavigate();
  const location = useLocation();
  const reportsMenuActive = useMemo(() => isReportsSubmenuPath(location.pathname), [location.pathname]);
  const feedbackReportsMenuActive = useMemo(() => isFeedbackReportsSubmenuPath(location.pathname), [location.pathname]);

  const getAdmin = async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_APILINK}/admin`, { headers: { token } });
    const data = await res.json();

    if (data && data.success) {
      setAdminUser(data.user);
      // Store admin user data in localStorage to avoid extra API calls
      localStorage.setItem('adminUser', JSON.stringify(data.user));
    } else {
      navigate('/admin/login');
    }
  };

  useEffect(() => {
    // Auto-collapse on mid-size screens; mobile uses overlay mode via breakPoint
    if (window.innerWidth >= 768 && window.innerWidth < 1024) {
      setIsCollapsed(true);
    }
  }, []);

  useEffect(() => {
    getAdmin();
  }, []);

  useEffect(() => {
    if (Number(adminUser.role) === 3) {
      setSelected('Guides');
    }
  }, [adminUser.role]);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <Box
      // style={{ position: 'fixed', top: 0, bottom: 0 }}
      sx={{
        '& .pro-sidebar-inner': {
          background: `${colors.primary[400]} !important`,
        },
        '& .pro-icon-wrapper': {
          backgroundColor: 'transparent !important',
        },
        '& .pro-inner-item': {
          padding: '5px 35px 5px 20px !important',
        },
        '& .pro-inner-item:hover': {
          color: '#868dfb !important',
        },
        '& .pro-menu-item.active': {
          color: '#6870fa !important',
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed} breakPoint="md" toggled={mobileOpen} onToggle={() => setMobileOpen(false)}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: '10px 0 20px 0',
              color: colors.grey[100],
            }}
          >
            {!isCollapsed && (
              <Box display="flex" justifyContent="space-between" alignItems="center" ml="15px">
                <Typography variant="h3" color={colors.grey[100]} sx={{ lineHeight: 1.2 }}>
                  Rapsody Travel
                </Typography>
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {/* {!isCollapsed && (
            <Box mb="25px">
              <Box textAlign="center">
                {adminUser?.username && (
                  <Typography variant="h6" color={colors.grey[100]} fontWeight="bold" sx={{ m: '5px 0 0 0' }}>
                    {adminUser?.username}
                  </Typography>
                )}
              </Box>
            </Box>
          )} */}

          <Box paddingLeft={isCollapsed ? undefined : '10%'}>
            {/* Coordinator (role === 3): Guides + Bookings + Coordinator Expenses */}
            {Number(adminUser.role) === 3 ? (
              <>
                <Item title="DASHBOARD" to="/" icon={<HomeOutlinedIcon />} selected={selected} setSelected={setSelected} />
                <Item
                  title="GUIDES"
                  to="/guide"
                  icon={<PeopleOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="BOOKINGS"
                  to="/booking"
                  icon={<CalendarTodayIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="EXPENSES"
                  to="/reports/my-reports"
                  icon={<AssessmentIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="ASSIGNED TRIPS"
                  to="/assigned-trips"
                  icon={<AssessmentIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <SubMenu
                  key={reportsMenuActive ? 'reports-open' : 'reports-closed'}
                  title="REPORTS"
                  icon={<AssessmentIcon />}
                  defaultOpen={reportsMenuActive}
                >
                  <Item title="- EXCURSION" to="/reports/excursion" selected={selected} setSelected={setSelected} />
                  <Item
                    title="- EXCURSION ATTENDANCE"
                    to="/reports/excursion-attendance"
                    selected={selected}
                    setSelected={setSelected}
                  />
                  <Item
                    title="- EXCURSION SUMMARY"
                    to="/reports/excursion-summary"
                    selected={selected}
                    setSelected={setSelected}
                  />
                  <Item
                    title="- GUIDE PERFORMANCE"
                    to="/reports/guide-performance"
                    selected={selected}
                    setSelected={setSelected}
                  />
                  <Item title="- DESTINATION" to="/reports/destinations" selected={selected} setSelected={setSelected} />
                </SubMenu>
                <SubMenu
                  key={feedbackReportsMenuActive ? 'feedback-reports-open' : 'feedback-reports-closed'}
                  title="FEEDBACK REPORTS"
                  icon={<QuizOutlinedIcon />}
                  defaultOpen={feedbackReportsMenuActive}
                >
                  <Item title="- SURVEY RESULTS" to="/reports/survey-results" selected={selected} setSelected={setSelected} />
                  <Item title="- SURVEY RESPONSES" to="/reports/survey-responses" selected={selected} setSelected={setSelected} />
                </SubMenu>
              </>
            ) : (
              <>
                {show('dashboard.view') && (
                  <Item title="DASHBOARD" to="/" icon={<HomeOutlinedIcon />} selected={selected} setSelected={setSelected} />
                )}

                {show('passengers.view') && (
                  <Item
                    title="PASSENGERS"
                    to="/allpassengers"
                    icon={<PeopleOutlinedIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}

                {show('destinations.view') && (
                  <Item
                    title="DESTINATION"
                    to="/countries"
                    icon={<PublicIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}
                {show('excursions.view') && (
                  <Item
                    title="EXCURSION"
                    to="/excursions"
                    icon={<FlightIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}
                {show('trips.view') && (
                  <Item
                    title="DESTINATION TRIP"
                    to="/country-excursion"
                    icon={<PlaceIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}
                {show('packages.view') && (
                <Item
                  title="PACKAGES"
                  to="/package"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-package-check-icon lucide-package-check"
                    >
                      <path d="m16 16 2 2 4-4" />
                      <path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14" />
                      <path d="m7.5 4.27 9 5.15" />
                      <polyline points="3.29 7 12 12 20.71 7" />
                      <line x1="12" x2="12" y1="22" y2="12" />
                    </svg>
                  }
                  selected={selected}
                  setSelected={setSelected}
                />
                )}

                {show('guides.view') && (
                  <Item
                    title="GUIDES"
                    to="/guide"
                    icon={<PeopleOutlinedIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}

                {show('bookings.view') && (
                  <Item
                    title="BOOKINGS"
                    to="/booking"
                    icon={<CalendarTodayIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}

                {show('promotions.view') && (
                <Item
                  title="PROMOTIONS"
                  to="/promotion"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-badge-percent"
                    >
                      <path d="M15 9h.01" />
                      <path d="M9 15h.01" />
                      <path d="m18.5 15-5-5" />
                      <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                  }
                  selected={selected}
                  setSelected={setSelected}
                />
                )}

                {show('surveys.view') && (
                  <Item
                    title="FEEDBACK QUESTIONS"
                    to="/feedback"
                    icon={<HelpOutlineIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}

                {show('user_feedback.view') && (
                  <Item
                    title="USER FEEDBACKS"
                    to="/userfeedback"
                    icon={<FeedbackIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}

                {show('messages.view') && (
                  <Item title="MESSAGES" to="/message" icon={<MessageIcon />} selected={selected} setSelected={setSelected} />
                )}

                {reportItems.length > 0 && (
                  <SubMenu
                    key={reportsMenuActive ? 'reports-open' : 'reports-closed'}
                    title="REPORTS"
                    icon={<AssessmentIcon />}
                    defaultOpen={reportsMenuActive}
                  >
                    {reportItems.map((r) => (
                      <Item key={r.to} title={r.title} to={r.to} selected={selected} setSelected={setSelected} />
                    ))}
                  </SubMenu>
                )}
                {feedbackReportItems.length > 0 && (
                  <SubMenu
                    key={feedbackReportsMenuActive ? 'feedback-reports-open' : 'feedback-reports-closed'}
                    title="FEEDBACK REPORTS"
                    icon={<QuizOutlinedIcon />}
                    defaultOpen={feedbackReportsMenuActive}
                  >
                    {feedbackReportItems.map((r) => (
                      <Item key={r.to} title={r.title} to={r.to} selected={selected} setSelected={setSelected} />
                    ))}
                  </SubMenu>
                )}
                {!customRole && (
                  <Item
                    title="ASSIGNED TRIPS"
                    to="/assigned-trips"
                    icon={<AssessmentIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}

                {show(['notifications.view', 'notifications.send', 'surveys.send']) && (
                  <Item
                    title="NOTIFICATIONS"
                    to="/notifications"
                    icon={<NotificationsOutlinedIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}

                {!customRole && (
                  <Item
                    title="PRIVACY POLICY"
                    to="/privacypolicy"
                    icon={<PrivacyTipIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}

                {Number(adminUser.role) === 1 && (
                  <Item
                    title="ADMIN USERS"
                    to="/adminusers"
                    icon={<VerifiedUserIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}

                {Number(adminUser.role) === 1 && (
                  <Item
                    title="ROLES & PERMISSIONS"
                    to="/roles"
                    icon={<VerifiedUserIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}

                {!customRole && (
                  <Item
                    title="CONFIGURATION"
                    to="/configuration"
                    icon={<SettingsOutlinedIcon />}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )}
              </>
            )}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
